import Keycloak, { type KeycloakInitOptions } from "keycloak-js";
import { runtimeConfig } from "@/config/runtime";

export class SessionError extends Error {
  public constructor(
    public readonly code: "AUTHENTICATION_REQUIRED" | "SESSION_EXPIRED",
    options?: ErrorOptions,
  ) {
    super(
      code === "AUTHENTICATION_REQUIRED" ? "Authentication is required" : "Your session expired",
      options,
    );
    this.name = "SessionError";
  }
}

export type SessionChangeReason =
  "authenticated" | "refreshed" | "anonymous" | "logout" | "expired";

export interface SessionProfileClaims {
  readonly givenName: string | null;
  readonly familyName: string | null;
}

type SessionListener = (authenticated: boolean, reason: SessionChangeReason) => void;

const OIDC_SCOPE = "openid profile email";

const keycloak = new Keycloak({
  url: runtimeConfig.keycloak.url,
  realm: runtimeConfig.keycloak.realm,
  clientId: runtimeConfig.keycloak.clientId,
});

export class KeycloakSession {
  private readonly listeners = new Set<SessionListener>();
  private initialization: Promise<boolean> | null = null;
  private refresh: Promise<string> | null = null;
  private locallySignedOut = false;

  public constructor(private readonly adapter: Keycloak) {
    this.adapter.onAuthSuccess = () => {
      if (!this.locallySignedOut) this.notify(true, "authenticated");
    };
    this.adapter.onAuthRefreshSuccess = () => {
      if (!this.locallySignedOut) this.notify(true, "refreshed");
    };
    this.adapter.onAuthRefreshError = () => {
      this.expireSession();
    };
    this.adapter.onAuthLogout = () => {
      this.locallySignedOut = true;
      this.notify(false, "logout");
    };
    this.adapter.onTokenExpired = () => {
      void this.getAccessToken().catch(() => undefined);
    };
  }

  public get authenticated(): boolean {
    return !this.locallySignedOut && this.adapter.authenticated === true;
  }

  public initialize(): Promise<boolean> {
    if (this.initialization === null) {
      const options: KeycloakInitOptions = {
        flow: "standard",
        pkceMethod: "S256",
        responseMode: "fragment",
        scope: OIDC_SCOPE,
        // Tokens deliberately remain in memory. Restore an existing Keycloak
        // SSO session after a full page load without showing the login screen.
        // Do not use Keycloak's hidden silent-check iframe: the realm's CSP
        // correctly blocks cross-origin framing. Without this option,
        // check-sso performs the equivalent top-level, non-prompting check.
        onLoad: "check-sso",
        // Disable the recurring session-status iframe as well.
        checkLoginIframe: false,
        enableLogging: runtimeConfig.keycloak.enableLogging,
      };
      this.initialization = this.adapter.init(options).then((authenticated) => {
        const effectiveAuthentication = authenticated && !this.locallySignedOut;
        this.notify(
          effectiveAuthentication,
          effectiveAuthentication ? "authenticated" : "anonymous",
        );
        return effectiveAuthentication;
      });
    }
    return this.initialization;
  }

  public async login(returnPath: string): Promise<void> {
    await this.adapter.login({
      redirectUri: this.safeRedirectUri(returnPath),
      scope: OIDC_SCOPE,
      // Every login in this client is initiated explicitly by the user. Asking
      // Keycloak to re-authenticate prevents a surviving realm SSO cookie from
      // silently restoring the identity that the user deliberately signed out.
      prompt: "login",
    });
  }

  public async register(returnPath: string): Promise<void> {
    await this.adapter.register({
      redirectUri: this.safeRedirectUri(returnPath),
      scope: OIDC_SCOPE,
    });
  }

  public async logout(): Promise<void> {
    this.locallySignedOut = true;
    this.notify(false, "logout");
    try {
      await this.adapter.logout({ redirectUri: `${window.location.origin}/` });
    } finally {
      // Keep local state safe even when the identity provider cannot complete
      // its redirect. The adapter has already built the logout request here.
      this.adapter.clearToken();
    }
  }

  public get profileClaims(): SessionProfileClaims {
    return {
      givenName: readClaim(
        this.adapter.idTokenParsed?.given_name,
        this.adapter.tokenParsed?.given_name,
      ),
      familyName: readClaim(
        this.adapter.idTokenParsed?.family_name,
        this.adapter.tokenParsed?.family_name,
      ),
    };
  }

  public getAccessToken(forceRefresh = false): Promise<string> {
    if (!this.authenticated) {
      return Promise.reject(new SessionError("AUTHENTICATION_REQUIRED"));
    }
    if (this.refresh === null) {
      this.refresh = this.refreshAccessToken(forceRefresh).finally(() => {
        this.refresh = null;
      });
    }
    return this.refresh;
  }

  public subscribe(listener: SessionListener): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  private async refreshAccessToken(forceRefresh: boolean): Promise<string> {
    try {
      await this.adapter.updateToken(forceRefresh ? -1 : 30);
    } catch (cause) {
      this.expireSession();
      throw new SessionError("SESSION_EXPIRED", { cause });
    }

    if (this.locallySignedOut) {
      throw new SessionError("AUTHENTICATION_REQUIRED");
    }
    if (typeof this.adapter.token !== "string" || this.adapter.token.length === 0) {
      this.expireSession();
      throw new SessionError("SESSION_EXPIRED");
    }
    return this.adapter.token;
  }

  private expireSession(): void {
    this.locallySignedOut = true;
    this.adapter.clearToken();
    this.notify(false, "expired");
  }

  private safeRedirectUri(returnPath: string): string {
    const candidate = new URL(returnPath, window.location.origin);
    return candidate.origin === window.location.origin
      ? candidate.toString()
      : `${window.location.origin}/`;
  }

  private notify(authenticated: boolean, reason: SessionChangeReason): void {
    for (const listener of this.listeners) {
      listener(authenticated, reason);
    }
  }
}

function readClaim(...values: unknown[]): string | null {
  for (const value of values) {
    if (typeof value === "string" && value.trim() !== "") {
      return value.trim();
    }
  }
  return null;
}

export const keycloakSession = new KeycloakSession(keycloak);
